import { useCallback, useEffect, useRef, useState } from 'react'
import { Upload, X, ImagePlus, Clipboard } from 'lucide-react'

interface ImageUploadProps {
  onFileSelected: (file: File) => void
  onClose: () => void
}

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']

export function ImageUpload({ onFileSelected, onClose }: ImageUploadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const dropZoneRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCounter = useRef(0)

  const isValidImage = (file: File) => ACCEPTED_TYPES.includes(file.type)

  const handleFile = useCallback(
    (file: File) => {
      if (isValidImage(file)) {
        onFileSelected(file)
      }
    },
    [onFileSelected]
  )

  // Handle drag events
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      dragCounter.current = 0

      const files = e.dataTransfer?.files
      if (files && files.length > 0) {
        const file = files[0]
        handleFile(file)
      }
    },
    [handleFile]
  )

  // Handle paste
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const files = e.clipboardData?.files
      if (files && files.length > 0) {
        const file = files[0]
        if (isValidImage(file)) {
          e.preventDefault()
          handleFile(file)
        }
      }
    }

    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [handleFile])

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Handle file input change
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFile(file)
      }
    },
    [handleFile]
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="relative w-full max-w-md mx-4 rounded-2xl border overflow-hidden"
        style={{
          backgroundColor: 'hsl(var(--color-bg-secondary))',
          borderColor: 'hsl(var(--color-border))',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'hsl(var(--color-border))' }}
        >
          <div className="flex items-center gap-2">
            <ImagePlus
              className="w-4 h-4"
              style={{ color: 'hsl(var(--color-text-secondary))' }}
            />
            <h2
              className="text-sm font-semibold"
              style={{ color: 'hsl(var(--color-text-primary))' }}
            >
              Add Image
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg transition-fast hover:opacity-80"
            style={{ color: 'hsl(var(--color-text-muted))' }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Drop zone */}
        <div className="p-5">
          <div
            ref={dropZoneRef}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className="rounded-xl border-2 border-dashed p-8 transition-fast text-center"
            style={{
              borderColor: isDragging
                ? 'hsl(var(--color-accent))'
                : 'hsl(var(--color-border))',
              backgroundColor: isDragging
                ? 'hsl(var(--color-accent) / 0.05)'
                : 'transparent',
            }}
          >
            <Upload
              className="w-8 h-8 mx-auto mb-3"
              style={{
                color: isDragging
                  ? 'hsl(var(--color-accent))'
                  : 'hsl(var(--color-text-muted))',
              }}
            />
            <p
              className="text-sm font-medium mb-1"
              style={{ color: 'hsl(var(--color-text-primary))' }}
            >
              {isDragging ? 'Drop image here' : 'Drag and drop an image'}
            </p>
            <div
              className="flex items-center justify-center gap-2 mb-4"
            >
              <Clipboard
                className="w-3.5 h-3.5"
                style={{ color: 'hsl(var(--color-text-muted))' }}
              />
              <p
                className="text-xs"
                style={{ color: 'hsl(var(--color-text-muted))' }}
              >
                or paste from clipboard
              </p>
            </div>

            {/* Select file button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-fast hover:opacity-90"
              style={{
                backgroundColor: 'hsl(var(--color-accent))',
                color: 'hsl(var(--color-bg-primary))',
              }}
            >
              Select from computer
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>

          {/* Supported formats hint */}
          <p
            className="text-xs text-center mt-3"
            style={{ color: 'hsl(var(--color-text-muted))' }}
          >
            PNG, JPG, GIF, or WebP up to 10MB
          </p>
        </div>
      </div>
    </div>
  )
}
